import {
  Accordion,
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  List,
  Loader,
  Stack,
  Text,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconBulb,
  IconCheck,
  IconCircle,
  IconCircleCheck,
  IconClock,
  IconPackage,
  IconTool,
} from '@tabler/icons-react'
import { AnimatedValue } from './AnimatedValue'
import { SparkleIndicator } from './SparkleIndicator'
import type { TemplateStep } from '../types'

interface ProjectGuideStepProps {
  step: TemplateStep
  index: number
  isModified?: boolean
  /** When false, show a short description under the title so users can skim collapsed steps. */
  isAccordionOpen?: boolean
  completed?: boolean
  onComplete?: (completed: boolean) => Promise<void>
  isUpdating?: boolean
}

export function ProjectGuideStep({
  step,
  index,
  isModified = false,
  isAccordionOpen = false,
  completed = false,
  onComplete,
  isUpdating = false,
}: ProjectGuideStepProps) {
  const stepNumber = index + 1
  const estimatedTime = step.estimatedMinutes
    ? step.estimatedMinutes >= 60
      ? `${Math.round(step.estimatedMinutes / 60)}h`
      : `${step.estimatedMinutes}m`
    : null

  const tools = step.tools?.map((t) => t.name ?? t.customName).filter(Boolean) ?? []
  const materials = step.materials?.map((m) => m.name ?? m.customName).filter(Boolean) ?? []

  return (
    <Accordion.Item value={`step-${stepNumber}`} style={completed ? { opacity: 0.75 } : undefined}>
      <Accordion.Control>
        <Group gap="md" wrap="nowrap">
          {onComplete ? (
            <ActionIcon
              variant="subtle"
              color={completed ? 'green' : 'gray'}
              size="lg"
              radius="xl"
              aria-label={completed ? 'Mark incomplete' : 'Mark complete'}
              loading={isUpdating}
              onClick={(e) => {
                e.stopPropagation()
                void onComplete(!completed)
              }}
            >
              {completed ? <IconCircleCheck size={28} /> : <IconCircle size={28} />}
            </ActionIcon>
          ) : (
            <Badge size="lg" variant={completed ? 'light' : 'filled'} color={completed ? 'green' : undefined} circle w={32} h={32}>
              {completed ? <IconCheck size={14} /> : stepNumber}
            </Badge>
          )}
          <Group gap="xs" align="flex-start" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text fw={500} lineClamp={2} td={completed ? 'line-through' : undefined} c={completed ? 'dimmed' : undefined}>
                {step.title}
              </Text>
              {step.description && !isAccordionOpen ? (
                <Text size="sm" c="dimmed" mt={4} lineClamp={2} style={{ lineHeight: 1.45 }}>
                  {step.description}
                </Text>
              ) : null}
            </Box>
            <SparkleIndicator
              visible={isModified}
              persistent
              ariaLabel="Step customized"
            />
          </Group>
          {estimatedTime && (
            <Group gap={4}>
              <IconClock size={14} color="var(--mantine-color-dimmed)" />
              <AnimatedValue value={estimatedTime} priority={2} size="sm" c="dimmed" />
            </Group>
          )}
        </Group>
      </Accordion.Control>

      <Accordion.Panel>
        <Stack gap="md" pl={{ base: 0, sm: 48 }}>
          <Text size="sm">{step.description}</Text>

          {/* Tools */}
          {tools.length > 0 && (
            <Box>
              <Group gap="xs" mb="xs">
                <IconTool size={14} color="var(--mantine-color-orange-6)" />
                <Text size="xs" fw={600} c="dimmed">
                  Tools
                </Text>
              </Group>
              <Group gap="xs" wrap="wrap">
                {tools.map((tool, i) => (
                  <Badge key={i} size="sm" variant="light" color="orange">
                    {tool}
                  </Badge>
                ))}
              </Group>
            </Box>
          )}

          {/* Materials */}
          {materials.length > 0 && (
            <Box>
              <Group gap="xs" mb="xs">
                <IconPackage size={14} color="var(--mantine-color-teal-6)" />
                <Text size="xs" fw={600} c="dimmed">
                  Materials
                </Text>
              </Group>
              <Group gap="xs" wrap="wrap">
                {materials.map((material, i) => (
                  <Badge key={i} size="sm" variant="light" color="teal">
                    {material}
                  </Badge>
                ))}
              </Group>
            </Box>
          )}

          {/* Tips */}
          {step.tips && step.tips.length > 0 && (
            <Card withBorder padding="sm" bg="var(--mantine-color-yellow-light)">
              <Group gap="xs" mb="xs">
                <IconBulb size={14} color="var(--mantine-color-yellow-6)" />
                <Text size="xs" fw={600}>
                  Tips
                </Text>
              </Group>
              <List size="sm" spacing="xs">
                {step.tips.map((tip, i) => (
                  <List.Item key={i}>
                    <Text size="sm">{tip}</Text>
                  </List.Item>
                ))}
              </List>
            </Card>
          )}

          {/* Warnings */}
          {step.warnings && step.warnings.length > 0 && (
            <Card withBorder padding="sm" bg="var(--mantine-color-red-light)">
              <Group gap="xs" mb="xs">
                <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
                <Text size="xs" fw={600} c="red.7">
                  Warning
                </Text>
              </Group>
              <List size="sm" spacing="xs">
                {step.warnings.map((warning, i) => (
                  <List.Item key={i}>
                    <Text size="sm">{warning}</Text>
                  </List.Item>
                ))}
              </List>
            </Card>
          )}
          {/* Step completion button — only shown for claimed projects */}
          {onComplete && (
            <Button
              variant={completed ? 'light' : 'filled'}
              color={completed ? 'gray' : 'green'}
              size="sm"
              leftSection={
                isUpdating ? (
                  <Loader size={14} color="currentColor" />
                ) : completed ? (
                  <IconCircleCheck size={16} />
                ) : (
                  <IconCheck size={16} />
                )
              }
              onClick={() => void onComplete(!completed)}
              disabled={isUpdating}
              style={{ alignSelf: 'flex-start' }}
            >
              {completed ? 'Completed' : 'Mark as done'}
            </Button>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  )
}
